import React, { useState } from 'react';
import './BulletPointsManagement.css';
import IconPicker from '../IconPicker';
import RichTextEditor from './RichTextEditor';

/**
 * 🎯 BULLET POINTS MANAGEMENT COMPONENT 2025
 * 
 * Moderne, togglebare Bullet Points Management Sektion mit:
 * - Sleek Toggle-Header mit Counter
 * - Glassmorphism Design
 * - Verbesserte Icon-Auswahl
 * - Responsive Grid Layout
 * - Brand-konforme Farbgebung
 */
function BulletPointsManagement({
    bulletPoints,
    addBulletPoint,
    removeBulletPoint,
    updateBulletPoint,
    handleBulletIconUpload,
    handleIconSelect,
    uploadingStates,
    iconPickerStates,
    setIconPickerState,
    editData
}) {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div className="bullet-points-management">
            {/* Modern Toggle Header */}
            <div 
                className={`bullet-points-header ${isExpanded ? 'expanded' : ''}`}
                onClick={toggleExpanded}
            >
                <div className="bullet-header-content">
                    <div className="bullet-header-icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            <circle cx="2" cy="4" r="1" fill="currentColor"/>
                            <circle cx="2" cy="8" r="1" fill="currentColor"/>
                            <circle cx="2" cy="12" r="1" fill="currentColor"/>
                        </svg>
                    </div>
                    <h3 className="bullet-header-title">Benefits</h3>
                    <div className="bullet-counter">
                        {bulletPoints.length}
                    </div>
                </div>
                <div className={`bullet-toggle-arrow ${isExpanded ? 'expanded' : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path 
                            d="M4 6L8 10L12 6" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="bullet-points-content">
                    {/* Add Button */}
                    <div className="add-bullet-section">
                        <button
                            onClick={addBulletPoint}
                            className="add-bullet-btn"
                        >
                            <div className="btn-icon">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M7 3V11M3 7H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <span>Add New Bullet Point</span>
                        </button>
                        <div className="add-bullet-hint">
                            Create compelling bullet points to highlight your key benefits
                        </div>
                    </div>

                    {/* Bullet Points Grid */}
                    {bulletPoints.length > 0 && (
                        <div className="bullet-points-grid">
                            {bulletPoints.map((bulletPoint, index) => (
                                <div key={bulletPoint.id || index} className="bullet-point-edit-card">
                                    {/* Card Header */}
                                    <div className="card-header">
                                        <div className="card-title">
                                            <div className="card-number">{index + 1}</div>
                                            <span>Bullet Point</span>
                                        </div>
                                        <button
                                            onClick={() => removeBulletPoint(index)}
                                            className="remove-btn"
                                            title="Remove bullet point"
                                        >
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                            </svg>
                                        </button>
                                    </div>

                                    {/* Icon Section - nur wenn nicht minimal/numbered Layout */}
                                    {editData.bulletLayout !== 'minimal' && editData.bulletLayout !== 'numbered' && (
                                        <div className="icon-section">
                                            <div className="icon-preview-container">
                                                <div 
                                                    className="icon-preview"
                                                    style={{
                                                        backgroundColor: editData.bulletIconBackgroundColor || 'rgba(102, 126, 234, 0.1)'
                                                    }}
                                                >
                                                    {(bulletPoint.iconType === 'upload' && bulletPoint.customIcon) || bulletPoint.icon ? (
                                                        <img
                                                            src={bulletPoint.iconType === 'upload' && bulletPoint.customIcon ? bulletPoint.customIcon : bulletPoint.icon}
                                                            alt="Bullet Icon"
                                                            className="icon-image"
                                                        />
                                                    ) : (
                                                        <div className="icon-placeholder">
                                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                <path d="M8 3V13M3 8H13" stroke="#dee2e6" strokeWidth="1.5" strokeLinecap="round"/>
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>
                                             
                                            </div>

                                            {/* Icon Controls */}
                                            <div className="icon-controls">
                                                <button
                                                    onClick={() => setIconPickerState(index, !iconPickerStates[index])}
                                                    className={`icon-control-btn picker ${iconPickerStates[index] ? 'active' : ''}`}
                                                    title="Choose from library"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                        <path d="M7 2L9 6H11L7 12L5 8H3L7 2Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
                                                    </svg>
                                                </button>

                                                <label className="icon-control-btn upload" title="Upload custom icon">
                                                    {uploadingStates[index] ? (
                                                        <div className="upload-spinner"></div>
                                                    ) : (
                                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                            <path d="M7 1V9M4 4L7 1L10 4M2 9V11C2 12.1 2.9 13 4 13H10C11.1 13 12 12.1 12 11V9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    )}
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => handleBulletIconUpload(index, e.target.files[0])}
                                                        style={{ display: 'none' }}
                                                    />
                                                </label>

                                                {(bulletPoint.icon || bulletPoint.customIcon) && (
                                                    <button
                                                        onClick={() => {
                                                            updateBulletPoint(index, 'icon', '');
                                                            updateBulletPoint(index, 'customIcon', '');
                                                        }}
                                                        className="icon-control-btn clear"
                                                        title="Clear icon"
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                            <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>

                                            {/* Icon Picker */}
                                            {iconPickerStates[index] && (
                                                <div className="icon-picker-container">
                                              
                                                    <div className="icon-picker-content">
                                                        <IconPicker
                                                            onSelect={(selectedIcon) => handleIconSelect(index, selectedIcon)}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                        </div>
                                    )}

                                    {/* Rich Text Editor */}
                                    <div className="content-section">
                                        <RichTextEditor
                                            label="Content"
                                            fieldName={`bulletPoint-${index}`}
                                            value={bulletPoint.richText}
                                            onChange={(e) => updateBulletPoint(index, 'richText', e.target.value)}
                                            placeholder="Enter your bullet point content with <strong>formatting</strong>..."
                                            height="150px"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                                    {/* Empty State */}
                    {bulletPoints.length === 0 && (
                        <div className="empty-state">
                            <div className="empty-state-icon">
                                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                    <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" strokeDasharray="3 3" opacity="0.3"/>
                                    <path d="M16 20h16M16 24h16M16 28h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
                                    <circle cx="12" cy="20" r="1.5" fill="currentColor" opacity="0.5"/>
                                    <circle cx="12" cy="24" r="1.5" fill="currentColor" opacity="0.5"/>
                                    <circle cx="12" cy="28" r="1.5" fill="currentColor" opacity="0.5"/>
                                </svg>
                            </div>
                            <h4>No Bullet Points Yet</h4>
                            <p>Click "Add New Bullet Point" to create your first compelling benefit point</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default BulletPointsManagement;
